import { type ReactNode } from 'react'
import cx from 'classnames'
import flattenChildren from 'react-keyed-flatten-children'
import { match } from '@/utils/pattern-match-util'

export interface ButtonGroupProps {
  /**
   * Whether to stack the buttons on top of each other.
   */
  stacked?: boolean

  /**
   * Whether to stretch the group to the available width in the parent.
   */
  stretch?: boolean

  /**
   * The buttons in the group.
   */
  children?: ReactNode

  /**
   * The spacing to use.
   */
  spacing?: 'base' | 'lg'

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Groups buttons together.
 */
export function ButtonGroup({
  children,
  stacked,
  stretch,
  spacing = 'base',
  testId,
}: ButtonGroupProps) {
  return (
    <div
      data-test={testId}
      className={cx(
        'flex',
        stacked && 'flex-col',
        stretch && 'w-full',
        match(spacing, {
          base: 'gap-base',
          lg: 'gap-lg',
        }),
      )}
    >
      {flattenChildren(children).map((c, i) => (
        <div
          className={cx(stretch ? 'flex flex-col flex-auto' : 'flex-auto')}
          key={i}
        >
          {c}
        </div>
      ))}
    </div>
  )
}
